import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const AUTO_ROTATE_MS = 6000;

/* ── Subcomponente: texto del anuncio con detección automática de recorte ── */
const SlideTextBlock = ({ content, onSeeMore }) => {
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // Con -webkit-line-clamp, scrollHeight = alto real del contenido completo,
      // clientHeight = alto visible ya recortado. Si son distintos, el texto no cupo.
      setIsOverflowing(el.scrollHeight - el.clientHeight > 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);
    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [content]);

  return (
    <>
      <SlideText ref={textRef}>{content}</SlideText>
      {/* Siempre visible: si el texto no se recortó dice "Ver", si se recortó dice "Ver más" */}
      <SeeMoreLink onClick={(e) => { e.stopPropagation(); onSeeMore(); }}>
        {isOverflowing ? 'Ver más' : 'Ver'}
      </SeeMoreLink>
    </>
  );
};

const AnnouncementCarousel = ({ theme }) => {
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [expandedBanner, setExpandedBanner] = useState(null); // banner abierto en el modal
  const [isHovered, setIsHovered] = useState(false);           // pausa al pasar el mouse
  const intervalRef = useRef(null);

  const isDarkMode = theme === 'dark';

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/banners`);
        setBanners(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error obteniendo banners:', error);
      } finally {
        setLoaded(true);
      }
    };
    fetchBanners();
  }, []);

  const goTo = useCallback((index) => {
    setActiveIndex((index + banners.length) % banners.length);
  }, [banners.length]);

  const goNext = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);
  const goPrev = useCallback(() => goTo(activeIndex - 1), [activeIndex, goTo]);

  // Auto-rotación: se pausa si el modal está abierto O si el mouse está sobre el carrusel
  useEffect(() => {
    if (banners.length <= 1 || expandedBanner || isHovered) return;
    intervalRef.current = setInterval(goNext, AUTO_ROTATE_MS);
    return () => clearInterval(intervalRef.current);
  }, [banners.length, goNext, expandedBanner, isHovered]);

  // Cierra el modal con la tecla Escape
  useEffect(() => {
    if (!expandedBanner) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setExpandedBanner(null); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [expandedBanner]);

  // Las flechas siempre funcionan, aunque esté en pausa por hover
  const handlePrevClick = (e) => { e.stopPropagation(); goPrev(); };
  const handleNextClick = (e) => { e.stopPropagation(); goNext(); };

  // No renderiza nada si aún no cargó, no hay banners, o el usuario lo cerró
  if (!loaded || banners.length === 0 || dismissed) return null;

  return (
    <CarouselWrapper
      active={isDarkMode}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CloseButton active={isDarkMode} onClick={() => setDismissed(true)} title="Cerrar">
        ✕
      </CloseButton>

      <Track>
        {banners.map((banner, i) => (
          <Slide key={banner._id} isActive={i === activeIndex} active={isDarkMode}>
            <MegaphoneIcon>📣</MegaphoneIcon>
            <SlideContent>
              <SlideTitle active={isDarkMode}>{banner.title}</SlideTitle>
              <SlideTextBlock
                content={banner.content}
                onSeeMore={() => setExpandedBanner(banner)}
              />
            </SlideContent>
          </Slide>
        ))}
      </Track>

      {banners.length > 1 && (
        <>
          <NavButton side="left" active={isDarkMode} onClick={handlePrevClick} aria-label="Anterior">
            ‹
          </NavButton>
          <NavButton side="right" active={isDarkMode} onClick={handleNextClick} aria-label="Siguiente">
            ›
          </NavButton>

          <Dots>
            {banners.map((_, i) => (
              <Dot
                key={i}
                isActive={i === activeIndex}
                active={isDarkMode}
                onClick={(e) => { e.stopPropagation(); goTo(i); }}
                aria-label={`Ir al anuncio ${i + 1}`}
              />
            ))}
          </Dots>
        </>
      )}

      {/* ── MODAL: muestra el anuncio completo, tal como se registró ── */}
      {expandedBanner && (
        <ModalOverlay onClick={() => setExpandedBanner(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalClose onClick={() => setExpandedBanner(null)} title="Cerrar">✕</ModalClose>
            <ModalIcon>📣</ModalIcon>
            <ModalTitle>{expandedBanner.title}</ModalTitle>
            <ModalText>{expandedBanner.content}</ModalText>
          </ModalBox>
        </ModalOverlay>
      )}
    </CarouselWrapper>
  );
};

/* ─── Styled Components ─── */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const CarouselWrapper = styled.div`
  position: relative;
  width: 92%;
  max-width: 1500px;
  margin: 40px auto 24px auto;
  border-radius: 16px;
  overflow: hidden;
  background: ${p => p.active
    ? 'linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #7c3aed 100%)'
    : 'linear-gradient(135deg, #a855f7 0%, #9333ea 50%, #7e22ce 100%)'};
  box-shadow: 0 8px 30px rgba(124, 58, 237, 0.35);
  animation: ${fadeIn} 0.3s ease;
  min-height: 100px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
      45deg,
      rgba(255,255,255,0.04) 0px,
      rgba(255,255,255,0.04) 2px,
      transparent 2px,
      transparent 14px
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 8% 50%, rgba(255,255,255,0.18) 0%, transparent 32%),
      radial-gradient(circle at 92% 50%, rgba(255,255,255,0.18) 0%, transparent 32%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    width: 94%;
    min-height: 110px;
    border-radius: 12px;
    margin-top: 10px;
  }
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  min-height: 90px;
  @media (max-width: 768px) { min-height: 100px; }
`;

const Slide = styled.div`
  position: ${p => p.isActive ? 'relative' : 'absolute'};
  top: 0; left: 0; right: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
  /* Un poco más de espacio abajo para que "Ver" no choque con los puntos */
  padding: 22px 70px 30px 70px;
  opacity: ${p => p.isActive ? 1 : 0};
  pointer-events: ${p => p.isActive ? 'auto' : 'none'};
  transition: opacity 0.4s ease;

  @media (max-width: 768px) {
    padding: 18px 50px 28px 50px;
    gap: 10px;
  }
`;

/* El megáfono va posicionado a la izquierda (fuera del flujo) para que
   el texto quede centrado exactamente en el carrusel, alineado con los puntos */
const MegaphoneIcon = styled.div`
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 28px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
  @media (max-width: 768px) {
    left: 14px;
    font-size: 22px;
  }
`;

const SlideContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
  min-width: 0;
`;

const SlideTitle = styled.span`
  font-size: 15px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.3px;
  text-shadow: 0 1px 3px rgba(0,0,0,0.25);
  @media (max-width: 768px) { font-size: 13px; }
`;

const SlideText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255,255,255,0.92);
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  // white-space: pre-line; /* respeta los saltos de línea con los que se registró, deja que el texto ocupe lo que pueda */
  @media (max-width: 768px) { font-size: 12px; -webkit-line-clamp: 4; }
`;

const SeeMoreLink = styled.button`
  background: transparent;
  border: none;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  padding: 2px 0 0 0;
  opacity: 0.85;
  &:hover { opacity: 1; }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  z-index: 6;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.18);
  color: #fff;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
  &:hover { background: rgba(255,255,255,0.32); }
`;

const NavButton = styled.button`
  position: absolute;
  bottom: 14px;
  ${p => p.side === 'left' ? 'left: 14px;' : 'right: 14px;'}
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
  &:hover { background: rgba(255,255,255,0.3); transform: scale(1.08); }
  @media (max-width: 768px) { width: 26px; height: 26px; font-size: 17px; }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 6px;
  z-index: 4;
  pointer-events: none; /* el contenedor no bloquea clics; los puntos sí */
`;

const Dot = styled.button`
  width: ${p => p.isActive ? '18px' : '6px'};
  height: 6px;
  border-radius: 4px;
  border: none;
  background: ${p => p.isActive ? '#fff' : 'rgba(255,255,255,0.4)'};
  cursor: pointer;
  padding: 0;
  pointer-events: auto;
  transition: all 0.25s ease;
`;

/* ─── Modal "Ver más" ─── */

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

const ModalBox = styled.div`
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  overflow-y: auto;
  background: linear-gradient(135deg, #4c1d95 0%, #6d28d9 50%, #7c3aed 100%);
  border-radius: 16px;
  padding: 32px 28px 28px 28px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
`;

const ModalClose = styled.button`
  position: absolute;
  top: 12px;
  right: 14px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.18);
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,0.32); }
`;

const ModalIcon = styled.div`
  font-size: 34px;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 800;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0,0,0,0.25);
`;

const ModalText = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255,255,255,0.95);
  line-height: 1.5;
  white-space: pre-line;
`;

export default AnnouncementCarousel;